<script setup>
import VideoTutorial from '@/components/VideoTutorial.vue'
import { computed, ref } from 'vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
    public: false, // butuh login
  },
})

// Data video tutorial — disusun mengikuti URUTAN BELAJAR (seriesOrder 1-27),
// dikelompokkan dalam 6 seri: A. Fondasi, B. Inventory Lanjutan, C. Pembelian,
// D. Operasional & F&B, E. Keuangan & Laporan, F. Administrasi Sistem.
// `category` tetap dipertahankan untuk filter UI yang sudah ada sebelumnya.
const videos = ref([
  // ── SERI A: Fondasi Setup ───────────────────────────────
  {
    id: 1,
    seriesOrder: 1,
    series: 'A. Fondasi',
    prerequisite: null,
    videoId: 'dQw4w9WgXcQ',
    title: 'Overview Setup Awal (Kategori, Satuan, Bahan Baku, Produk)',
    description: 'Alur setup dari nol: kategori, satuan ukuran, bahan baku, hingga produk pertama Anda.',
    duration: '10:00',
    category: 'Inventory',
    module: 'Setup Awal',
    route: 'userguide-produk-inventory-kategori',
    isNew: false,
    icon: 'ri-flag-2-line',
  },
  {
    id: 2,
    seriesOrder: 2,
    series: 'A. Fondasi',
    prerequisite: 1,
    videoId: 'dQw4w9WgXcQ',
    title: 'Deep Dive: Bahan Baku',
    description: 'Input ingredient, satuan, stok minimum, dan cara membaca detail bahan baku.',
    duration: '8:00',
    category: 'Inventory',
    module: 'Bahan Baku',
    route: 'userguide-produk-inventory-bahan-baku',
    isNew: false,
    icon: 'ri-flask-line',
  },
  {
    id: 3,
    seriesOrder: 3,
    series: 'A. Fondasi',
    prerequisite: 2,
    videoId: 'dQw4w9WgXcQ',
    title: 'Produk, Varian & Recipe (BOM)',
    description: 'Menghubungkan bahan baku ke produk lewat Recipe — jantung sistem inventory PintuKasir.',
    duration: '11:00',
    category: 'Inventory',
    module: 'Produk',
    route: 'userguide-produk-inventory-produk',
    isNew: false,
    icon: 'ri-cup-line',
  },
  {
    id: 4,
    seriesOrder: 4,
    series: 'A. Fondasi',
    prerequisite: 3,
    videoId: 'dQw4w9WgXcQ',
    title: 'Modifier Group — Topping & Pilihan Tambahan',
    description: 'Tambah topping, level pedas/gula, dan pilihan opsional lain tanpa bikin varian baru.',
    duration: '6:30',
    category: 'Inventory',
    module: 'Modifier',
    route: 'userguide-produk-inventory-modifier',
    isNew: true,
    icon: 'ri-add-circle-line',
  },

  // ── SERI B: Inventory Lanjutan ──────────────────────────
  {
    id: 5,
    seriesOrder: 5,
    series: 'B. Inventory Lanjutan',
    prerequisite: 3,
    videoId: 'dQw4w9WgXcQ',
    title: 'Stock Adjustment — Koreksi Stok Manual',
    description: 'Koreksi stok saat tumpah, rusak, kadaluarsa, atau bonus dari supplier.',
    duration: '6:30',
    category: 'Inventory',
    module: 'Stock Adjustment',
    route: 'userguide-produk-inventory-stock-adjustment',
    isNew: false,
    icon: 'ri-edit-2-line',
  },
  {
    id: 6,
    seriesOrder: 6,
    series: 'B. Inventory Lanjutan',
    prerequisite: 5,
    videoId: 'dQw4w9WgXcQ',
    title: 'Stock Opname — Audit Stok Menyeluruh',
    description: 'Cocokkan stok fisik dengan sistem secara rutin lewat 5 langkah opname.',
    duration: '9:00',
    category: 'Inventory',
    module: 'Stock Opname',
    route: 'userguide-produk-inventory-stock-opname',
    isNew: false,
    icon: 'ri-clipboard-line',
  },
  {
    id: 7,
    seriesOrder: 7,
    series: 'B. Inventory Lanjutan',
    prerequisite: 6,
    videoId: 'dQw4w9WgXcQ',
    title: 'Riwayat Pergerakan Stok',
    description: 'Buku besar digital setiap keluar-masuk bahan baku — pembelian, penjualan, adjustment, opname.',
    duration: '5:30',
    category: 'Inventory',
    module: 'Pergerakan Stok',
    route: 'userguide-produk-inventory-pergerakan-stok',
    isNew: false,
    icon: 'ri-swap-line',
  },

  // ── SERI C: Pembelian (Pembelian) ────────────────────
  {
    id: 8,
    seriesOrder: 8,
    series: 'C. Pembelian',
    prerequisite: 2,
    videoId: 'dQw4w9WgXcQ',
    title: 'Supplier Management',
    description: 'Daftarkan supplier lengkap dengan kontak dan alamat sebelum membuat Purchase Order.',
    duration: '4:30',
    category: 'Pembelian',
    module: 'Supplier',
    route: 'userguide-pembelian-supplier',
    isNew: true,
    icon: 'ri-truck-line',
  },
  {
    id: 9,
    seriesOrder: 9,
    series: 'C. Pembelian',
    prerequisite: 8,
    videoId: 'dQw4w9WgXcQ',
    title: 'Purchase Order — Pesan ke Supplier',
    description: 'Buat PO, tentukan jumlah & harga, dan pantau status Draft hingga Received.',
    duration: '7:30',
    category: 'Pembelian',
    module: 'Purchase Order',
    route: 'userguide-pembelian-purchase-order',
    isNew: false,
    icon: 'ri-file-list-3-line',
  },
  {
    id: 10,
    seriesOrder: 10,
    series: 'C. Pembelian',
    prerequisite: 9,
    videoId: 'dQw4w9WgXcQ',
    title: 'Receiving — Terima Barang dari Supplier',
    description: 'Verifikasi fisik barang datang, update HPP rata-rata, dan tambah stok resmi.',
    duration: '7:30',
    category: 'Pembelian',
    module: 'Receiving',
    route: 'userguide-pembelian-receiving',
    isNew: false,
    icon: 'ri-download-cloud-2-line',
  },
  {
    id: 11,
    seriesOrder: 11,
    series: 'C. Pembelian',
    prerequisite: 10,
    videoId: 'dQw4w9WgXcQ',
    title: 'Scan Struk AI — Terima Barang Otomatis',
    description: 'Foto struk supplier, AI ekstrak dan cocokkan otomatis ke bahan baku Anda.',
    duration: '5:30',
    category: 'Pembelian',
    module: 'Scan Struk AI',
    route: 'userguide-pembelian-scan-struk-ai',
    isNew: true,
    icon: 'ri-scan-2-line',
  },

  // ── SERI D: Operasional & F&B ────────────────────────────
  {
    id: 12,
    seriesOrder: 12,
    series: 'D. Operasional & F&B',
    prerequisite: 4,
    videoId: 'dQw4w9WgXcQ',
    title: 'Shift & POS Kasir',
    description: 'Buka shift, alur transaksi lengkap, void, hingga tutup shift & rekonsiliasi kas.',
    duration: '11:00',
    category: 'Operasional',
    module: 'POS Kasir',
    route: 'userguide-operasional-pos-kasir',
    isNew: true,
    icon: 'ri-shopping-cart-2-line',
  },
  {
    id: 13,
    seriesOrder: 13,
    series: 'D. Operasional & F&B',
    prerequisite: 12,
    videoId: 'dQw4w9WgXcQ',
    title: 'AI Voice Order — Pesan Lewat Suara',
    description: 'Cara setup dan pakai fitur pesan lewat suara — tips agar akurasi maksimal.',
    duration: '5:45',
    category: 'Operasional',
    module: 'AI Voice Order',
    route: 'userguide-operasional-ai-voice-order',
    isNew: true,
    icon: 'ri-mic-line',
  },
  {
    id: 14,
    seriesOrder: 14,
    series: 'D. Operasional & F&B',
    prerequisite: 12,
    videoId: 'dQw4w9WgXcQ',
    title: 'Tipe Order & Biaya Layanan Tambahan',
    description: 'Dine-in, Take Away, atau Delivery — dan bagaimana biaya tambahan terhitung otomatis.',
    duration: '5:30',
    category: 'F&B Modern',
    module: 'Tipe Order',
    route: 'userguide-fb-modern-order-type',
    isNew: true,
    icon: 'ri-percent-line',
  },
  {
    id: 15,
    seriesOrder: 15,
    series: 'D. Operasional & F&B',
    prerequisite: 14,
    videoId: 'dQw4w9WgXcQ',
    title: 'Integrasi GoFood, GrabFood & ShopeeFood',
    description: 'Setup platform delivery agar order online otomatis masuk ke POS.',
    duration: '7:30',
    category: 'F&B Modern',
    module: 'Platform Delivery',
    route: 'userguide-fb-modern-delivery-platforms',
    isNew: true,
    icon: 'ri-motorbike-line',
  },
  {
    id: 16,
    seriesOrder: 16,
    series: 'D. Operasional & F&B',
    prerequisite: 12,
    videoId: 'dQw4w9WgXcQ',
    title: 'Denah Meja, Sesi & Split Bill',
    description: 'Buat floor plan, buka/gabung/pindah sesi meja, dan pisah pembayaran per grup.',
    duration: '8:30',
    category: 'F&B Modern',
    module: 'Meja & Area',
    route: 'userguide-fb-modern-tables',
    isNew: true,
    icon: 'ri-table-2',
  },
  {
    id: 17,
    seriesOrder: 17,
    series: 'D. Operasional & F&B',
    prerequisite: 16,
    videoId: 'dQw4w9WgXcQ',
    title: 'Self-Order via Scan QR',
    description: 'Pelanggan pesan sendiri dari HP lewat QR meja — mode Tamu atau Member.',
    duration: '7:30',
    category: 'F&B Modern',
    module: 'Self-Order',
    route: 'userguide-fb-modern-self-order',
    isNew: true,
    icon: 'ri-qr-code-line',
  },
  {
    id: 18,
    seriesOrder: 18,
    series: 'D. Operasional & F&B',
    prerequisite: 16,
    videoId: 'dQw4w9WgXcQ',
    title: 'Kitchen Display System & Stasiun Dapur',
    description: 'Routing tiket ke stasiun yang tepat, status pesanan, dan indikator waktu tunggu.',
    duration: '8:30',
    category: 'F&B Modern',
    module: 'Kitchen Display',
    route: 'userguide-fb-modern-kds',
    isNew: false,
    icon: 'ri-fire-line',
  },
  {
    id: 19,
    seriesOrder: 19,
    series: 'D. Operasional & F&B',
    prerequisite: 18,
    videoId: 'dQw4w9WgXcQ',
    title: 'Setup Printer — Bluetooth & Jaringan',
    description: 'Koneksi printer thermal via Bluetooth/WiFi, setting printer dapur, dan printer label.',
    duration: '7:40',
    category: 'Sistem',
    module: 'Printer',
    route: 'userguide-sistem-printers',
    isNew: false,
    icon: 'ri-printer-line',
  },

  // ── SERI E: Keuangan & Laporan ───────────────────────────
  {
    id: 20,
    seriesOrder: 20,
    series: 'E. Keuangan & Laporan',
    prerequisite: 12,
    videoId: 'dQw4w9WgXcQ',
    title: 'Kategori & Pencatatan Pengeluaran',
    description: 'Input pengeluaran harian: listrik, gaji, sewa, dan kategori lainnya.',
    duration: '5:30',
    category: 'Keuangan',
    module: 'Pengeluaran',
    route: 'userguide-keuangan-pengeluaran',
    isNew: false,
    icon: 'ri-wallet-3-line',
  },
  {
    id: 21,
    seriesOrder: 21,
    series: 'E. Keuangan & Laporan',
    prerequisite: 20,
    videoId: 'dQw4w9WgXcQ',
    title: 'Dashboard Laporan Bisnis',
    description: 'Overview penjualan, produk terlaris, dan stok kritis dalam satu layar.',
    duration: '5:30',
    category: 'Laporan',
    module: 'Dashboard Laporan',
    route: 'userguide-laporan-dashboard',
    isNew: true,
    icon: 'ri-dashboard-2-line',
  },
  {
    id: 22,
    seriesOrder: 22,
    series: 'E. Keuangan & Laporan',
    prerequisite: 21,
    videoId: 'dQw4w9WgXcQ',
    title: 'Laporan Penjualan & Inventory',
    description: 'Detail transaksi per periode dan status stok lengkap, siap diekspor.',
    duration: '6:30',
    category: 'Laporan',
    module: 'Laporan Penjualan',
    route: 'userguide-laporan-penjualan',
    isNew: false,
    icon: 'ri-line-chart-line',
  },
  {
    id: 23,
    seriesOrder: 23,
    series: 'E. Keuangan & Laporan',
    prerequisite: 22,
    videoId: 'dQw4w9WgXcQ',
    title: 'Analisa Laporan Laba Rugi',
    description: 'Baca laporan P&L, pahami HPP snapshot, dan temukan area optimasi profit.',
    duration: '8:20',
    category: 'Laporan',
    module: 'Laba Rugi',
    route: 'userguide-laporan-laba-rugi',
    isNew: false,
    icon: 'ri-funds-line',
  },

  // ── SERI F: Administrasi Sistem ──────────────────────────
  {
    id: 24,
    seriesOrder: 24,
    series: 'F. Administrasi Sistem',
    prerequisite: 1,
    videoId: 'dQw4w9WgXcQ',
    title: 'Pengaturan Toko & Pajak',
    description: 'Konfigurasi nama toko, alamat, pajak, dan format struk.',
    duration: '6:00',
    category: 'Sistem',
    module: 'Pengaturan',
    route: 'userguide-sistem-pengaturan',
    isNew: false,
    icon: 'ri-settings-3-line',
  },
  {
    id: 25,
    seriesOrder: 25,
    series: 'F. Administrasi Sistem',
    prerequisite: 24,
    videoId: 'dQw4w9WgXcQ',
    title: 'Manajemen User & Role',
    description: 'Tambah karyawan, atur hak akses kasir vs manager vs owner.',
    duration: '8:00',
    category: 'Sistem',
    module: 'User & Role',
    route: 'userguide-sistem-user-role',
    isNew: false,
    icon: 'ri-user-settings-line',
  },
  {
    id: 26,
    seriesOrder: 26,
    series: 'F. Administrasi Sistem',
    prerequisite: 24,
    videoId: 'dQw4w9WgXcQ',
    title: 'Notifikasi Sistem',
    description: 'Peringatan otomatis stok rendah, laporan siap, hingga aktivitas mencurigakan.',
    duration: '4:30',
    category: 'Sistem',
    module: 'Notifikasi',
    route: 'userguide-sistem-notifikasi',
    isNew: true,
    icon: 'ri-notification-3-line',
  },
  {
    id: 27,
    seriesOrder: 27,
    series: 'F. Administrasi Sistem',
    prerequisite: 25,
    videoId: 'dQw4w9WgXcQ',
    title: 'Audit Log — Jejak Aktivitas',
    description: 'Lacak siapa mengubah apa dan kapan — jejak yang tidak bisa dihapus.',
    duration: '5:30',
    category: 'Sistem',
    module: 'Audit Log',
    route: 'userguide-sistem-audit-log',
    isNew: true,
    icon: 'ri-history-line',
  },
])

// Search & filter
const searchQuery = ref('')
const selectedCategory = ref('Semua')

// Color mapping per kategori (sesuai menu)
const categoryColors = {
  'Operasional': 'success',
  'Inventory': 'info',
  'F&B Modern': 'primary',
  'Pembelian': 'warning',
  'Keuangan': 'error',
  'Laporan': 'secondary',
  'Sistem': 'grey',
}

const categories = computed(() => {
  const cats = ['Semua', ...new Set(videos.value.map(v => v.category))]
  return cats
})

const filteredVideos = computed(() => {
  return videos.value
    .filter(video => {
      const q = searchQuery.value.toLowerCase()
      const matchSearch = !q ||
        video.title.toLowerCase().includes(q) ||
        video.description.toLowerCase().includes(q) ||
        video.module.toLowerCase().includes(q)
      const matchCategory = selectedCategory.value === 'Semua' ||
        video.category === selectedCategory.value
      return matchSearch && matchCategory
    })
    .sort((a, b) => a.seriesOrder - b.seriesOrder)
})

// Stats
const stats = computed(() => ({
  total: videos.value.length,
  new: videos.value.filter(v => v.isNew).length,
  categories: categories.value.length - 1,
}))

// Active video
const activeVideoId = ref(null)
const playVideo = (videoId) => {
  activeVideoId.value = videoId
}
const closeVideo = () => {
  activeVideoId.value = null
}
</script>

<template>
  <VContainer fluid class="pa-4 pa-md-6">
    <!-- Header Section -->
    <VRow class="mb-6">
      <VCol cols="12">
        <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between gap-4">
          <div>
            <h1 class="text-h4 text-sm-h3 font-weight-bold mb-2">
              📹 Video Tutorial PintuKasir
            </h1>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Pelajari semua fitur PintuKasir lewat video singkat per modul
            </p>
          </div>
          <div class="d-flex gap-2 flex-wrap">
            <VChip color="primary" size="large" variant="elevated">
              <VIcon start icon="mdi-play-circle" />
              {{ stats.total }} Video
            </VChip>
            <VChip color="error" size="large" variant="tonal">
              <VIcon start icon="mdi-sparkles" />
              {{ stats.new }} Baru
            </VChip>
          </div>
        </div>
      </VCol>
    </VRow>

    <!-- Search & Filter -->
    <VRow class="mb-6">
      <VCol cols="12" md="7">
        <VTextField
          v-model="searchQuery"
          placeholder="Cari video (contoh: POS, stok, printer, laba rugi...)"
          prepend-inner-icon="ri-search-line"
          variant="outlined"
          density="comfortable"
          clearable
          hide-details
          bg-color="surface"
        />
      </VCol>
      <VCol cols="12" md="5">
        <VSelect
          v-model="selectedCategory"
          :items="categories"
          label="Filter Kategori"
          variant="outlined"
          density="comfortable"
          hide-details
          prepend-inner-icon="ri-filter-3-line"
          bg-color="surface"
        />
      </VCol>
    </VRow>

    <!-- Category Quick Filter (chips) -->
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex flex-wrap gap-2">
          <VChip
            v-for="cat in categories"
            :key="cat"
            :color="cat === 'Semua' ? 'primary' : categoryColors[cat]"
            :variant="selectedCategory === cat ? 'elevated' : 'tonal'"
            :prepend-icon="cat === 'Semua' ? 'mdi-view-grid' : undefined"
            @click="selectedCategory = cat"
            class="category-chip"
          >
            {{ cat }}
          </VChip>
        </div>
      </VCol>
    </VRow>

    <!-- Video Grid -->
    <VRow>
      <VCol
        v-for="video in filteredVideos"
        :key="video.id"
        cols="12"
        sm="6"
        lg="4"
        xl="3"
      >
        <VCard
          class="video-card h-100"
          elevation="2"
          hover
        >
          <!-- Badge "Baru" -->
          <VChip
            v-if="video.isNew"
            color="error"
            size="x-small"
            class="position-absolute top-0 right-0 ma-2 badge-new"
            variant="elevated"
          >
            BARU
          </VChip>

          <!-- Video / Thumbnail -->
          <div class="video-wrapper">
            <VideoTutorial
              v-if="activeVideoId === video.videoId"
              :video-id="video.videoId"
              :title="video.title"
            />
            <div v-else class="thumbnail-placeholder" @click="playVideo(video.videoId)">
              <VImg
                :src="`https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`"
                cover
                aspect-ratio="16/9"
                class="thumbnail-img"
              />
              <div class="play-overlay">
                <VIcon
                  icon="mdi-play-circle"
                  size="56"
                  color="white"
                  class="play-icon"
                />
              </div>
              <VChip
                color="black"
                size="x-small"
                class="position-absolute bottom-0 right-0 ma-2"
                variant="elevated"
              >
                ⏱ {{ video.duration }}
              </VChip>
            </div>
          </div>

          <!-- Content -->
          <VCardItem class="pb-2">
            <VCardTitle class="text-subtitle-1 font-weight-bold line-clamp-2">
              {{ video.title }}
            </VCardTitle>
            <template v-slot:append>
              <VChip
                :color="categoryColors[video.category]"
                size="x-small"
                variant="tonal"
              >
                {{ video.module }}
              </VChip>
            </template>
          </VCardItem>

          <VCardText class="pt-0">
            <p class="text-body-2 text-medium-emphasis line-clamp-2 mb-2">
              {{ video.description }}
            </p>
            <div class="d-flex align-center gap-1">
              <VIcon :icon="video.icon" size="16" class="text-medium-emphasis" />
              <span class="text-caption text-medium-emphasis">{{ video.category }}</span>
            </div>
          </VCardText>

          <!-- Actions -->
          <VCardActions class="px-4 pb-4">
            <VBtn
              color="primary"
              variant="tonal"
              size="small"
              prepend-icon="mdi-play"
              block
              @click="playVideo(video.videoId)"
            >
              Tonton
            </VBtn>
            <VBtn
              v-if="video.route"
              color="secondary"
              variant="text"
              size="small"
              icon="mdi-book-open-variant"
              :to="{ name: video.route }"
              title="Baca panduan tertulis"
            />
          </VCardActions>
        </VCard>
      </VCol>
    </VRow>

    <!-- Empty State -->
    <VRow v-if="filteredVideos.length === 0">
      <VCol cols="12">
        <VCard class="text-center pa-8" variant="tonal" color="secondary">
          <VIcon icon="mdi-video-off" size="64" color="grey" class="mb-4" />
          <h3 class="text-h6 mb-2">Tidak ada video ditemukan</h3>
          <p class="text-body-2 text-medium-emphasis mb-0">
            Coba ubah kata kunci pencarian atau filter kategori
          </p>
        </VCard>
      </VCol>
    </VRow>

    <!-- Info Card -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard color="primary" variant="tonal">
          <VCardText>
            <div class="d-flex align-center justify-space-between flex-wrap gap-4">
              <div>
                <h6 class="text-h6 mb-1">💡 Butuh bantuan lebih lanjut?</h6>
                <p class="text-body-2 mb-0">
                  Baca panduan tertulis lengkap di menu
                  <RouterLink to="/userguide" class="font-weight-bold text-primary">
                    User Guide
                  </RouterLink>
                </p>
              </div>
              <VChip color="primary" size="large" variant="elevated" :to="{ name: 'userguide' }">
                Buka User Guide
              </VChip>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </VContainer>
</template>

<style lang="scss" scoped>
.video-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border-radius: 12px !important;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
  }
}

.video-wrapper {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  background: rgba(var(--v-theme-on-surface), 0.05);
  overflow: hidden;
}

.thumbnail-placeholder {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.thumbnail-img {
  transition: transform 0.3s ease;

  .video-card:hover & {
    transform: scale(1.05);
  }
}

.play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.3s ease;

  .video-card:hover & {
    opacity: 1;
  }
}

.play-icon {
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  transition: transform 0.3s ease;

  .video-card:hover & {
    transform: scale(1.1);
  }
}

.badge-new {
  z-index: 1;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.category-chip {
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@media (max-width: 600px) {
  .video-card:hover {
    transform: none;
  }
}
</style>
